import { Mail } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, Callout, RoleBadge, pathFor,
  Diagram, MockField, MockButton, MockRow,
} from "../shared";

export default function SettingsEmailSection() {
  return (
    <div data-testid="guide-settings-email">
      <PageHeading
        icon={Mail}
        title="Email Settings (SMTP)"
        description="This is the mailbox the system uses to send every email it sends you — handoff alerts, sign-off requests, reminders, password resets, and IT alerts. If email isn't going out, this is where you fix it."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="it_admin" />
        <span className="text-xs text-white/40">Needs the <span className="font-mono text-white/70">manage_smtp</span> permission. Saving the username and password asks you to re-enter your own password first.</span>
      </div>

      <Callout variant="info" title="In plain words">
        Just like your phone needs your email account details before it can send mail, the system needs a mailbox to send from. SMTP is simply the technical name for "outgoing mail". You type in the mailbox details once, test them, and save.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          When you first set up the system, or when emails suddenly stop arriving (often because the mailbox password was changed). You enter the details, press Test, and Save.
        </p>
      </Section>

      <Section title="The screen">
        <Diagram
          frameLabel="Settings → Email (SMTP)"
          legend={[
            <>Fill in the mailbox details (server, port, username, password, from-address).</>,
            <>Press <span className="text-white">Test Connection</span> to check the details connect — no email is sent.</>,
            <>Press <span className="text-white">Send Test Email</span> to send yourself a real test message.</>,
            <>Press <span className="text-white">Save</span> and confirm with your password.</>,
          ]}
        >
          <MockRow>
            <MockField label="Host" value="smtp.office365.com" marker={1} />
            <MockField label="Port" value="587" />
          </MockRow>
          <MockField label="Username" value="governance@example.com" />
          <MockField label="From address" value="governance@example.com" />
          <MockRow>
            <MockButton label="Test Connection" marker={2} variant="ghost" />
            <MockButton label="Send Test Email" marker={3} variant="ghost" />
            <MockButton label="Save" marker={4} />
          </MockRow>
        </Diagram>
      </Section>

      <Section title="Each field explained">
        <div className="space-y-2">
          <Field name="Host" required="required"><p>The mail server's address (for example <span className="text-white">smtp.office365.com</span> or <span className="text-white">smtp.gmail.com</span>). Your email provider tells you this.</p></Field>
          <Field name="Port" required="required"><p>The "door number" for the mail server. Usually <span className="text-white">587</span>, sometimes <span className="text-white">465</span>, rarely <span className="text-white">25</span>.</p></Field>
          <Field name="Use TLS / Secure" required="optional"><p>A security tick box. Turn it on for port 465. Leave it off for port 587 — the system secures the connection automatically.</p></Field>
          <Field name="Username" required="required"><p>The mailbox login name. Note: IT can set this from outside the app using an environment setting called <span className="text-white">SMTP_USERNAME</span>. If they do, this box is locked and shows a small badge.</p></Field>
          <Field name="Password" required="required"><p>The mailbox password. Same outside-setting behaviour using <span className="text-white">SMTP_PASSWORD</span>. For your safety it is never shown back to you once saved.</p></Field>
          <Field name="From address" required="required"><p>The address every email appears to come from. It must be one the mailbox is allowed to send as.</p></Field>
          <Field name="From name" required="optional"><p>The friendly name shown next to the address (e.g. "Governance System"). Defaults to <span className="text-white">Governance System</span>.</p></Field>
          <Field name="Notification URL" required="optional"><p>An optional override for the website link placed inside emails. If left blank, the Base URL from Branding is used.</p></Field>
          <Field name="Enabled" required="required"><p>The master on/off switch. When off, no email goes out at all — even reminders and IT alerts are paused.</p></Field>
        </div>
      </Section>

      <Section title="How to save and test">
        <Steps steps={[
          <>Fill in or update the fields shown above.</>,
          <>Press <span className="text-white">Test Connection</span> — the system tries to reach the mail server without sending anything. If it fails you get a plain hint (login failed / connection refused / security error).</>,
          <>Press <span className="text-white">Send Test Email</span> and pick who should receive it. A real email is sent so you can confirm everything works end to end.</>,
          <>Press <span className="text-white">Save</span> and confirm with your own password. The change takes effect straight away.</>,
        ]} />
      </Section>

      <Callout variant="info" title="IT alert recipients live in the same tab">
        Just below the email card you'll find the IT Alert Recipients card. It's explained on its own page because it only shares the mailbox login — see <Link href={pathFor("settings/alert-dispatcher")} className="text-cyan-300 hover:underline">IT Alert Dispatcher</Link>.
      </Callout>
      <Callout variant="warning" title="An outside setting wins over this screen">
        If IT has set <span className="text-white">SMTP_USERNAME</span> / <span className="text-white">SMTP_PASSWORD</span> outside the app, those win and the boxes here are ignored (but kept). To use the values on this screen again, IT must remove those outside settings and restart.
      </Callout>
    </div>
  );
}
